import container from './container';

const mboxes = Object.keys(container);

/**
 * Response tokens attached to a single Adobe Target offer option. Keys are the
 * dotted analytics identifiers returned by the Adobe Target SDK.
 */
type ResponseTokens = {
    'profile.tntId'?: string;
    'activity.id'?: string;
    'activity.name'?: string;
    'experience.id'?: string;
    'experience.name'?: string;
};

/** A single offer option (an FBC mbox option or a VEC pageLoad option). */
export type OfferOption = {
    content?: { identifier?: string; variant?: string };
    type?: string;
    responseTokens?: ResponseTokens;
};

/** A single Form Based Composer mbox entry within the offers response. */
type OfferMbox = {
    name?: string;
    options?: OfferOption[];
};

/**
 * Minimal shape of the Adobe Target `getOffers` response that this app reads.
 * Every level is optional because the SDK only populates the branches for the
 * experiments that are actually active.
 */
export type AdobeOffers = {
    execute?: {
        mboxes?: OfferMbox[];
        pageLoad?: { options?: OfferOption[] };
    };
};

/** A single experiment token param spread into the quotation request body. */
type ExperimentTokenParam = {
    tntId?: string;
    activityId?: string;
    activityName?: string;
    experienceId?: string;
    experienceName?: string;
};

/** Adobe experiment params (the `at` payload) spread into the quotation body. */
export type AdobeExperimentParams = {
    at: Record<string, never> | ExperimentTokenParam[];
};

export const isAdobeOffers = (value: unknown): value is AdobeOffers => typeof value === 'object' && value !== null;

const hasTokens = (token: ResponseTokens | undefined): token is ResponseTokens => Boolean(token);

// We expect the options array to only ever have one element
export const findMbox = (offers: unknown, mbox: string): OfferOption | undefined => {
    if (!isAdobeOffers(offers)) return undefined;
    return offers.execute?.mboxes?.find((x) => x.name === mbox)?.options?.[0];
};

// Whereas in the VEC field these correspond to each experiment
export const findVec = (offers: unknown): OfferOption[] | undefined => {
    if (!isAdobeOffers(offers)) return undefined;
    return offers.execute?.pageLoad?.options;
};

// We can have many FBC experiments active at once
export const formTokens = (offers: unknown): ResponseTokens[] =>
    mboxes.map((mbox) => findMbox(offers, mbox)?.responseTokens).filter(hasTokens);

// We can also have many VEC experiments active at once but if none are active return empty array
export const vecTokens = (offers: unknown): ResponseTokens[] =>
    findVec(offers)
        ?.map((vec) => vec?.responseTokens)
        .filter(hasTokens) ?? [];

// The `at` array is sent to quotations-web-orch, which only accepts a limited number of experiments per request
// and drops the rest. Be mindful of this if you're adding mboxes/experiments or otherwise growing `at`.
export const experimentParams = (offers: unknown): AdobeExperimentParams | undefined => {
    if (!offers) return undefined;

    // Combine logging for FBC and VEC experiments
    // If both are undefined, this just becomes an empty array
    const tokens = [...vecTokens(offers), ...formTokens(offers)].filter(hasTokens);

    if (tokens.length === 0) return { at: {} };

    const value = tokens.map((token) => {
        return {
            tntId: token['profile.tntId'],
            activityId: token['activity.id'],
            activityName: token['activity.name'],
            experienceId: token['experience.id'],
            experienceName: token['experience.name']
        };
    });

    return { at: value };
};
